import React, { useMemo } from "react";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import { Loader } from "@campgladiator/cgui-core.atoms.loader";
import { CreateMembershipModalSteps } from "../CreateMembershipModal";
import { SelectedTrainer } from "../../SelectTrainerInput/SelectTrainerInput";
import { fetchSubscriptions } from "@campgladiator/cg-common.api.subscriptions";
import { SelectedCouponOption } from "../../SelectCouponInput/useSelectCouponInput";

export type UseCreateMembershipModalButtonsProps = {
  isLoading: boolean;
  isScheduleRequired: boolean;
  isTermChecked: boolean;
  onClose?: (success?: boolean) => void;
  paymentStartDate: string;
  selectedCouponOption: SelectedCouponOption;
  selectedPaymentMethodId: string;
  selectedProductOption: Option;
  selectedScheduleOption: Option;
  selectedTrainer: SelectedTrainer;
  sellingTrainerId: string;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setIsTermChecked: (isTermChecked: boolean) => void;
  setSelectedPaymentMethodId: (paymentMethodId: string) => void;
  setStep: (step: CreateMembershipModalSteps) => void;
  step: CreateMembershipModalSteps;
  stripeCustomerId: string;
  theme: ButtonProps["theme"];
  trialDays: number;
  userId: string;
};

const useCreateMembershipModalButtons = ({
  step,
  isLoading,
  selectedProductOption,
  selectedTrainer,
  selectedScheduleOption,
  isScheduleRequired,
  trialDays,
  selectedPaymentMethodId,
  paymentStartDate,
  isTermChecked,
  setIsSubmitting,
  setStep,
  onClose,
  theme,
  userId,
  stripeCustomerId,
  sellingTrainerId,
  selectedCouponOption,
  setSelectedPaymentMethodId,
  setIsTermChecked,
}: UseCreateMembershipModalButtonsProps) => {
  const isNextButtonValid = useMemo(() => {
    const isSelectProductValid =
      !isLoading &&
      selectedProductOption &&
      selectedTrainer &&
      (isScheduleRequired ? !!selectedScheduleOption : true) &&
      trialDays >= 0;
    const isSelectPaymentMethodValid = !isLoading && !!selectedPaymentMethodId;
    const isConfirmValid = !!selectedProductOption && !!selectedTrainer;
    const isTermsValid = !isLoading && isTermChecked;

    switch (step) {
      case "selectProduct":
        return isSelectProductValid;
      case "selectPaymentMethod":
        return isSelectPaymentMethodValid;
      case "confirm":
        return isConfirmValid;
      case "terms":
        return isTermsValid;
    }
  }, [
    isLoading,
    isTermChecked,
    selectedProductOption,
    selectedTrainer,
    selectedScheduleOption,
    trialDays,
    selectedPaymentMethodId,
    step,
  ]);

  const handleSubmit = () => {
    setIsSubmitting(true);
    const payload = {
      userId,
      stripeCustomerId,
      salesRep: sellingTrainerId,
      productId: selectedProductOption?.metadata?.stripeProductId,
      paymentMethodId: selectedPaymentMethodId,
      couponCode: selectedCouponOption?.id,
      trialPeriodDays: trialDays,
      programId: selectedScheduleOption?.metadata?.programId,
      periodStartDate: new Date(paymentStartDate).toISOString(),
      productPrice: selectedProductOption?.metadata?.prices[0].stripePriceId,
      programScheduleId: selectedScheduleOption?.metadata?.id,
    };
    fetchSubscriptions(payload)
      .then(() => {
        setIsSubmitting(false);
        onClose && onClose(true);
      })
      .catch(() => {
        setIsSubmitting(false);
        onClose && onClose(false);
      });
  };

  const getTitleBasedOnStep = () => {
    switch (step) {
      case "selectProduct":
        return "Create Membership";
      case "selectPaymentMethod":
        return "Select Payment Method";
      case "confirm":
        return "Confirm new membership";
      case "terms":
        return "Bold BIG 5";
    }
  };

  const getNextStep = (): CreateMembershipModalSteps => {
    switch (step) {
      case "selectProduct":
        return "selectPaymentMethod";
      case "selectPaymentMethod":
        return "confirm";
      case "confirm":
        return "terms";
      case "terms":
        return undefined;
    }
  };

  const getPreviousStep = (): CreateMembershipModalSteps => {
    switch (step) {
      case "selectProduct":
        return undefined;
      case "selectPaymentMethod":
        return "selectProduct";
      case "confirm":
        return "selectPaymentMethod";
      case "terms":
        return "confirm";
    }
  };

  const getCancelButtonLabel = () => {
    return step === "selectProduct" ? "Cancel" : "Back";
  };

  const getNextButtonLabel = () => {
    if (isLoading) return <Loader />;
    switch (step) {
      case "selectProduct":
        return "Continue";
      case "selectPaymentMethod":
        return "Continue";
      case "confirm":
        return "Confirm";
      case "terms":
        return "Complete Purchase";
    }
  };

  const handlePreviousButtonClick = () => {
    const previousStep = getPreviousStep();
    if (previousStep) {
      setStep(previousStep);
      if (previousStep === "selectProduct") {
        setSelectedPaymentMethodId(undefined);
      }
    } else {
      onClose && onClose(false);
    }
    setIsTermChecked(false);
  };

  const cancelButton: ButtonProps = {
    theme,
    emphasis: "secondary",
    variation: "text",
    children: getCancelButtonLabel(),
    onClick: handlePreviousButtonClick,
  };

  const handleNextButtonClick = () => {
    const nextStep = getNextStep();
    if (nextStep) {
      setStep(nextStep);
    } else {
      handleSubmit();
    }
  };
  const nextButton: ButtonProps = {
    theme,
    emphasis: "primary",
    children: getNextButtonLabel(),
    onClick: handleNextButtonClick,
    disabled: isLoading || !isNextButtonValid,
  };

  const buttons: ButtonProps[] = [cancelButton, nextButton];

  return { buttons, getTitleBasedOnStep };
};

export default useCreateMembershipModalButtons;
